import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LandingHeader from '../00-shared/LandingHeader';
import LandingFooter from '../00-shared/LandingFooter';
import { GlobalStyles } from '../00-shared/styles';
import { highlightColor } from '../00-shared/constants';
import { blogPosts, blogCategories, getFeaturedPost, getBlogPostsByCategory, type BlogCategory } from './blogData';

const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const BlogListingPage: React.FC = () => {
    const [activeCategory, setActiveCategory] = useState<'All' | BlogCategory>('All');
    const featured = getFeaturedPost();
    const filteredPosts = getBlogPostsByCategory(activeCategory);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('fade-in-up');
                }
            });
        }, observerOptions);

        document.querySelectorAll('.observe-fade').forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, [activeCategory]);

    return (
        <div className="min-h-screen bg-black text-white transition-colors duration-300" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif" }}>
            <Helmet>
                <title>Blog - CGM Sports</title>
                <meta name="title" content="Blog - CGM Sports | AI-Powered Performance Analytics" />
                <meta name="description" content="Explore how CGM Sports helps coaches, clinicians, athletic directors, personal trainers, and athletes with AI-powered wearable analytics. Guides, product updates, and best practices." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://cgmsports.com/blog" />
                <meta property="og:title" content="Blog - CGM Sports | AI-Powered Performance Analytics" />
                <meta property="og:description" content="Explore how CGM Sports helps coaches, clinicians, athletic directors, personal trainers, and athletes with AI-powered wearable analytics." />
                <meta property="og:image" content="https://cgmsports.com/logos/SOCIALMEDIA-WHITE.png" />
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Blog - CGM Sports | AI-Powered Performance Analytics" />
                <meta property="twitter:description" content="Explore how CGM Sports helps coaches, clinicians, athletic directors, personal trainers, and athletes with AI-powered wearable analytics." />
                <link rel="canonical" href="https://cgmsports.com/blog" />
            </Helmet>
            <GlobalStyles />
            <LandingHeader highlightColor={highlightColor} />

            <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
                <div className="max-w-7xl mx-auto relative z-10">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 observe-fade opacity-0">Blog</h1>
                    <p className="text-gray-300 text-lg max-w-2xl observe-fade opacity-0">
                        Product updates, guides, and insights for every role in performance analytics.
                    </p>
                </div>
            </section>

            {/* Featured post — always mounted, animated via CSS transition on category switch */}
            {featured && (
                <section
                    className="px-4 sm:px-6 lg:px-8 bg-black overflow-hidden transition-all duration-500 ease-in-out"
                    style={{
                        maxHeight: activeCategory === 'All' ? '500px' : '0px',
                        opacity: activeCategory === 'All' ? 1 : 0,
                        paddingBottom: activeCategory === 'All' ? '3rem' : '0px',
                    }}
                >
                    <div className="max-w-7xl mx-auto">
                        <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Featured</p>
                        <Link
                            to={`/blog/${featured.slug}`}
                            className="block group"
                        >
                            <div className="bg-neutral-900 border border-neutral-800 transition-all duration-300 group-hover:border-neutral-600 overflow-hidden md:flex">
                                <div className="md:w-2/5 h-56 md:h-auto">
                                    <img
                                        src={featured.heroImage}
                                        alt={featured.title}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        loading="eager"
                                    />
                                </div>
                                <div className="p-8 md:p-12 md:w-3/5">
                                    <span className="text-xs uppercase tracking-widest text-gray-500">{featured.category}</span>
                                    <h2 className="text-2xl md:text-3xl font-bold text-white mt-2 mb-3 group-hover:text-gray-200 transition-colors">
                                        {featured.title}
                                    </h2>
                                    <p className="text-gray-400 text-base max-w-3xl mb-4">{featured.subtitle}</p>
                                    <span className="text-sm text-gray-500">{formatDate(featured.date)}</span>
                                </div>
                            </div>
                        </Link>
                    </div>
                </section>
            )}

            {/* Category filter tabs */}
            <section className="pb-4 px-4 sm:px-6 lg:px-8 bg-black">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-wrap gap-2 border-b border-neutral-800 pb-4">
                        {blogCategories.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`px-4 py-2 text-sm font-medium transition-all duration-200 cursor-pointer ${
                                    activeCategory === cat
                                        ? 'text-black bg-white'
                                        : 'text-gray-400 hover:text-white bg-transparent border border-neutral-700 hover:border-neutral-500'
                                }`}
                                style={{ borderRadius: 0 }}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Post list */}
            <section className="py-8 px-4 sm:px-6 lg:px-8 bg-black">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredPosts.map((post) => (
                            <Link
                                key={post.slug}
                                to={`/blog/${post.slug}`}
                                className="block group observe-fade opacity-0"
                            >
                                <article className="bg-neutral-900/50 border border-neutral-800 h-full flex flex-col transition-all duration-300 group-hover:border-neutral-600 group-hover:bg-neutral-900 overflow-hidden">
                                    <div className="h-44 overflow-hidden">
                                        <img
                                            src={post.heroImage}
                                            alt={post.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-xs uppercase tracking-widest text-gray-500">{post.category}</span>
                                            <span className="text-xs text-gray-600">{formatDate(post.date)}</span>
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-gray-200 transition-colors leading-snug">
                                            {post.title}
                                        </h3>
                                        <p className="text-sm text-gray-400 flex-1">{post.subtitle}</p>
                                        <div className="mt-4 flex items-center text-sm text-gray-500">
                                            <span>{post.author}</span>
                                        </div>
                                    </div>
                                </article>
                            </Link>
                        ))}
                    </div>

                    {filteredPosts.length === 0 && (
                        <div className="text-center py-20">
                            <p className="text-gray-500 text-lg">No posts in this category yet.</p>
                        </div>
                    )}
                </div>
            </section>

            <LandingFooter highlightColor={highlightColor} />
        </div>
    );
};

export default BlogListingPage;
